import { UkDateInput, ukDate } from "@/components/UkDateInput";

import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserCircle, Save, Plane, Plus, Upload, FileText, Download, X, Camera } from "lucide-react";
import { HeaderAccent } from "@/lib/status";
import { AvailabilityEditor } from "@/components/AvailabilityEditor";
import { ActivityGallery } from "@/components/ActivityGallery";
import { WorkPrefsEditor } from "@/components/WorkPrefsEditor";
import { MyStaffAgreement, MyContracts, MyContractChoice } from "@/components/StaffAgreements";
import { ChangePasswordCard } from "@/components/ChangePassword";

const STATUS = { pending: "bg-gold-light text-gold-dark", approved: "bg-sage-light text-sage-dark", declined: "bg-coral-light text-coral-dark" };
const inp = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

export default function Account() {
  const { user, refreshUser } = useAuth();
  const [p, setP] = useState(null);
  const [pay, setPay] = useState({ bank_account: "", ni_number: "" });
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [photo, setPhoto] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);

  const loadProfile = () => api.get("/me/profile").then((r) => {
    const d = r.data; const ec = d.emergency_contact || {};
    setPhoto(d.photo || "");
    setP({ name: d.name || "", email: d.email || "", phone: d.phone || "", address: d.address || "", dob: d.dob || "",
      emergency_contact_name: ec.name || "", emergency_contact_phone: ec.phone || "", emergency_contact_relationship: ec.relationship || "",
      bio: d.bio || "", interests: d.interests || "", activities: d.activities || "",
      work_areas: d.work_areas || [], offered_services: d.offered_services || [],
      availability: d.availability || [], blocked_dates: d.blocked_dates || [] });
    setPay({ bank_account: d.bank_account || "", ni_number: d.ni_number || "" });
  }).catch(() => {});
  const loadLeave = () => api.get("/leave").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { loadProfile(); loadLeave(); }, []);

  const onPhoto = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setPhotoBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto(r.data.photo); toast.success("Photo updated"); refreshUser && refreshUser();
    } catch (err) { toast.error(apiError(err)); } finally { setPhotoBusy(false); e.target.value = ""; }
  };

  const saveProfile = async () => {
    try { await api.put("/me/profile", p); toast.success("Personal details saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const savePay = async () => {
    try { await api.put("/me/payroll", pay); toast.success("Payroll details saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const downloadCert = async (lid, name) => {
    try {
      const r = await api.get(`/leave/${lid}/certificate`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); window.open(url, "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!p) return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" /></div>;

  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));
  const F = ({ label, k, type = "text" }) => (
    <div><label className="mb-1 block text-sm font-medium text-ink">{label}</label>
      <input type={type} className={inp} value={p[k]} onChange={(e) => set(k, e.target.value)} data-testid={`account-${k}`} /></div>
  );

  return (
    <div className="space-y-6" data-testid="account-page">
      <div>
        <HeaderAccent />
        <h1 className="font-heading text-3xl font-bold tracking-tight text-ink flex items-center gap-2"><UserCircle size={28} className="text-brand" /> My account</h1>
        <p className="mt-1 text-ink-soft">Keep your details, availability and time off up to date.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {["carer", "office"].includes(user?.role) && <MyContractChoice />}
        {["carer", "office"].includes(user?.role) && <MyStaffAgreement />}
        {["carer", "office"].includes(user?.role) && <MyContracts />}
        <ChangePasswordCard />
        <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
          <h2 className="mb-4 font-heading text-lg font-semibold text-ink">Personal details</h2>
          <div className="mb-5 flex items-center gap-4">
            {photo
              ? <img src={photo} alt="Profile" className="h-20 w-20 rounded-full object-cover ring-2 ring-brand/20" data-testid="account-photo" />
              : <span className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-light text-2xl font-bold text-brand" data-testid="account-photo-placeholder">{(p.name || "?").charAt(0)}</span>}
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-ink hover:bg-secondary">
              <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="account-photo-input" />
              <Camera size={16} /> {photoBusy ? "Uploading…" : (photo ? "Change photo" : "Add photo")}
            </label>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <F label="Full name" k="name" />
            <div><label className="mb-1 block text-sm font-medium text-ink">Email</label><input className={`${inp} bg-secondary`} value={p.email} disabled /></div>
            <F label="Phone" k="phone" />
            <div><label className="mb-1 block text-sm font-medium text-ink">Date of birth</label><UkDateInput dob value={p.dob} max={new Date().toISOString().slice(0, 10)} onChange={(v) => set("dob", v)} testid="account-dob" /></div>
            <div className="sm:col-span-2"><label className="mb-1 block text-sm font-medium text-ink">Home address</label><input className={inp} value={p.address} onChange={(e) => set("address", e.target.value)} data-testid="account-address" /></div>
            <F label="Next of kin — name" k="emergency_contact_name" />
            <F label="Next of kin — phone" k="emergency_contact_phone" />
            <F label="Next of kin — relationship" k="emergency_contact_relationship" />
          </div>
          <button onClick={saveProfile} data-testid="save-profile-button" className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white hover:bg-brand-dark"><Save size={18} /> Save details</button>
        </div>

        {user?.role === "carer" && (
          <div className="rounded-xl border border-border bg-white p-6 shadow-sm lg:col-span-2" data-testid="about-me-card">
            <h2 className="font-heading text-lg font-semibold text-ink">About me</h2>
            <p className="mb-4 mt-1 text-sm text-ink-soft">This is shown to the families you support, so they can get to know you. Keep it warm and friendly.</p>
            <div className="grid grid-cols-1 gap-4">
              <div><label className="mb-1 block text-sm font-medium text-ink">A short introduction</label>
                <textarea rows={3} className={inp} value={p.bio} onChange={(e) => set("bio", e.target.value)} data-testid="account-bio" placeholder="e.g. Hi, I'm Maria. I've been befriending for 5 years and love a good chat over a cuppa." /></div>
              <div><label className="mb-1 block text-sm font-medium text-ink">Things I enjoy / interests</label>
                <textarea rows={2} className={inp} value={p.interests} onChange={(e) => set("interests", e.target.value)} data-testid="account-interests" placeholder="e.g. Gardening, classic films, baking, local history" /></div>
              <div><label className="mb-1 block text-sm font-medium text-ink">Activities I like to do on visits</label>
                <textarea rows={2} className={inp} value={p.activities} onChange={(e) => set("activities", e.target.value)} data-testid="account-activities" placeholder="e.g. Walks in the park, board games, trips to the garden centre, crafts" /></div>
            </div>
            <button onClick={saveProfile} data-testid="save-about-button" className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white hover:bg-brand-dark"><Save size={18} /> Save about me</button>
            <div className="mt-6 border-t border-border pt-5">
              <h3 className="font-heading text-base font-semibold text-ink">Activity photos</h3>
              <p className="mb-3 mt-1 text-sm text-ink-soft">Share up to 3 friendly photos of things you enjoy. The office approves each one before families can see it.</p>
              <ActivityGallery staffId={user.id} />
            </div>
          </div>
        )}

        {user?.role === "carer" && (
          <div className="rounded-xl border border-border bg-white p-6 shadow-sm lg:col-span-2" data-testid="work-prefs-card">
            <h2 className="font-heading text-lg font-semibold text-ink">Where I work &amp; what I offer</h2>
            <p className="mb-4 mt-1 text-sm text-ink-soft">Pick the areas you can travel to and the services you're happy to provide. The office uses this to match you with clients.</p>
            <WorkPrefsEditor value={p} onChange={(v) => setP(v)} />
            <button onClick={saveProfile} data-testid="save-work-prefs-button" className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white hover:bg-brand-dark"><Save size={18} /> Save areas &amp; services</button>
          </div>
        )}

        <div className="space-y-6">
          <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
            <h2 className="mb-4 font-heading text-lg font-semibold text-ink">Payroll details</h2>
            <div className="space-y-3">
              <div><label className="mb-1 block text-sm font-medium text-ink">Bank details (name · sort code · account no.)</label><input className={inp} value={pay.bank_account} onChange={(e) => setPay({ ...pay, bank_account: e.target.value })} data-testid="account-bank" placeholder="e.g. Halifax · 11-22-33 · 12345678" /></div>
              <div><label className="mb-1 block text-sm font-medium text-ink">National Insurance number</label><input className={inp} value={pay.ni_number} onChange={(e) => setPay({ ...pay, ni_number: e.target.value })} data-testid="account-ni" placeholder="e.g. QQ 12 34 56 C" /></div>
            </div>
            <button onClick={savePay} data-testid="save-payroll-button" className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white hover:bg-brand-dark"><Save size={18} /> Save payroll</button>
          </div>

          <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
            <h2 className="mb-4 font-heading text-lg font-semibold text-ink">My availability</h2>
            <AvailabilityEditor kind="staff" id={user.id} initial={p.availability} initialBlocked={p.blocked_dates} onSaved={loadProfile} />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-ink"><Plane size={18} className="text-brand" /> My time off &amp; sickness</h2>
          <button onClick={() => setOpen(true)} data-testid="account-request-timeoff" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white hover:bg-brand-dark"><Plus size={18} /> Request time off</button>
        </div>
        {rows.length === 0 ? <p className="text-ink-muted">You haven't requested any time off yet.</p> : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.id} data-testid={`account-leave-${r.id}`} className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-ink">{ukDate(r.start_date)} → {ukDate(r.end_date)} {r.kind === "sickness" && <span className="ml-1 rounded-full bg-coral-light px-2 py-0.5 text-[11px] font-semibold text-coral-dark">Sickness</span>}</p>
                  {r.reason && <p className="text-sm text-ink-soft">{r.reason}</p>}
                </div>
                {r.certificate && (
                  <button onClick={() => downloadCert(r.id)} data-testid={`view-cert-${r.id}`} className="inline-flex items-center gap-1 text-sm font-medium text-slateblue hover:underline"><FileText size={14} /> Certificate</button>
                )}
                {r.kind === "sickness" && !r.certificate && <CertUpload lid={r.id} onDone={loadLeave} />}
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${STATUS[r.status]}`}>{r.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {open && <RequestModal userId={user.id} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); loadLeave(); }} />}
    </div>
  );
}

function CertUpload({ lid, onDone }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/leave/${lid}/certificate`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Certificate uploaded"); onDone(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-ink hover:bg-secondary">
      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} data-testid={`upload-cert-${lid}`} />
      <Upload size={14} /> {busy ? "Uploading…" : "Sick note"}
    </label>
  );
}

function RequestModal({ userId, onClose, onSaved }) {
  const [f, setF] = useState({ kind: "leave", start_date: "", end_date: "", reason: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.start_date || !f.end_date) { toast.error("Choose both dates"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/leave", { ...f, subject_type: "staff", subject_id: userId });
      if (file && f.kind === "sickness") {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/leave/${data.id}/certificate`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Request submitted"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 max-h-[92vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-xl" data-testid="account-leave-modal">
        <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-xl font-bold text-ink">Request time off</h2><button onClick={onClose}><X size={20} className="text-ink-muted" /></button></div>
        <div className="space-y-3">
          <div className="flex gap-2">
            {[["leave", "Holiday / leave"], ["sickness", "Sickness / absence"]].map(([k, lbl]) => (
              <button key={k} onClick={() => setF({ ...f, kind: k })} className={`flex-1 rounded-lg py-2 text-sm font-semibold ${f.kind === k ? (k === "sickness" ? "bg-coral text-white" : "bg-brand text-white") : "border border-border text-ink-soft"}`}>{lbl}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <div className="flex-1"><label className="mb-1 block text-xs font-medium text-ink-muted">From</label><UkDateInput value={f.start_date} onChange={(v) => setF({ ...f, start_date: v })} testid="account-leave-start" /></div>
            <div className="flex-1"><label className="mb-1 block text-xs font-medium text-ink-muted">To</label><UkDateInput value={f.end_date} min={f.start_date} onChange={(v) => setF({ ...f, end_date: v })} testid="account-leave-end" /></div>
          </div>
          <textarea rows={2} className={inp} placeholder="Reason (optional)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
          {f.kind === "sickness" && (
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-ink-soft hover:bg-secondary">
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="account-leave-cert" />
              <Upload size={15} /> {file ? file.name : "Attach sick certificate (optional)"}
            </label>
          )}
          <button onClick={submit} disabled={busy} data-testid="account-submit-leave" className="w-full rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark disabled:opacity-60">{busy ? "Submitting…" : "Submit request"}</button>
        </div>
      </div>
    </div>
  );
}
